import {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo
} from "react";
import API from "api";
import Session from "helpers/session";
import {
  DataGridPro,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import {
  Box,
  Snackbar,
  Alert,
  Tooltip
} from "@mui/material";
import moment from "moment";
import Button from "components/shared/Button/Button";
import InvalidateModal from "components/shared/InvalidateModal/InvalidateModal";
import { GlobalContext} from "context/Provider";
import { StyledDot } from "views/Main/Main.styles";
import { ABBREV_TO_DISPLAY } from "helpers/variables";

declare module "@mui/x-data-grid-pro" {
  interface ToolbarPropsOverrides {
    selectedCount: number;
    onBulkClick: () => void;
  }
}

interface ReadingRow {
  id: string;
  memberId: string;
  externId: string;
  type: string;
  value: string;
  valueDetails?: string[];
  measuredAt: string;
  breached: boolean;
  invalid: boolean;
  reason?: string;
}

const SPIR_LABELS: Record<string, string> = {
  fev: "FEV1",
  fvc: "FVC",
  "fev/fvc": "FEV1/FVC",
  pef: "PEF",
};

function formatDetails(reading: any): string[] | undefined {
  if (reading.type !== "spir") return undefined;
  const metrics = reading.metrics ?? {};
  return Object.entries(SPIR_LABELS)
    .filter(([key]) => metrics[key] != null)
    .map(([key, label]) => `${label}: ${metrics[key]}`);
}

function CustomToolbar({ selectedCount, onBulkClick }: { selectedCount: number; onBulkClick: () => void }) {
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </Box>
      {selectedCount > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 1, pb: 1 }}>
          <span>{selectedCount} selected</span>
          <Box sx={{ p: 0.5 }}>
            <Button variant="warning" height="36px" onClick={onBulkClick}>
              Update Validity
            </Button>
          </Box>
        </Box>
      )}
    </GridToolbarContainer>
  );
}

export default function Readings() {
  const [rows, setRows] = useState<ReadingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetReading, setTargetReading] = useState<ReadingRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: "success" | "error" } | null>(null);
  const { provider } = useContext(GlobalContext);
  const selectedRows = rows.filter((reading) => selectedIds.includes(reading.id));
  const toInvalidate = selectedRows.filter((reading) => !reading.invalid);
  const toRevalidate = selectedRows.filter((reading) => reading.invalid);
  const invalidateCount = toInvalidate.length;
  const revalidateCount = toRevalidate.length;

  const memberMap = useMemo(() => {
    const map: Record<string, string> = {};
    (provider?.memberList ?? []).forEach((member: any) => {
      const fullName =
        member.lastName || member.firstName
          ? `${member.lastName ?? ""}, ${member.firstName ?? ""}`.replace(/^, |, $/g, "").trim()
          : member.name ?? "-";
      map[member.id] = fullName;
    });
    return map;
  }, [provider?.memberList]);

  const loadReadings = useCallback(() => {
    setLoading(true);
    API.kore.readings
      .getAll(
        Session.crackCookieToken("provider"),
        undefined,
        false,
        "id,memberId,externId,updatedAt,type,measuredAt,metrics,recordedAt,registeredAt,uploadedAt,userId,clientId,source,sourceId,createdAt,organization,breached,breachType,validation",
        "all"
      )
      .then((res: any) => {
        const items = res?.items ?? res ?? [];
        setRows(items.map((reading: any) => formatRow(reading)));
      })
      .catch((err: any) => {
        setSnack({ msg: "Couldn't load readings", severity: "error" });
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "memberName",
      headerName: "Member",
      flex: 2,
      minWidth: 160,
      valueGetter: (_value, row) => memberMap[row.memberId] ?? row.externId ?? "-",
    },
    {
      field: "type",
      headerName: "Type",
      flex: 2,
      minWidth: 80,
      valueGetter: (value: any) => ABBREV_TO_DISPLAY[value] ?? value ?? "-",
    },
    {
      field: "value",
      headerName: "Reading",
      flex: 2,
      minWidth: 130,
      renderCell: (params) => {
        const details = params.row.valueDetails;
        if (!details?.length) return params.value;
        return (
          <Tooltip
            arrow
            placement="top"
            title={<Box sx={{ whiteSpace: "pre-line" }}>{details.join("\n")}</Box>}
          >
            <span style={{ cursor: "default" }}>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "measuredAt",
      headerName: "Measured",
      flex: 2,
      minWidth: 160,
      type: "dateTime",
      valueGetter: (value: any) => (value ? new Date(value) : null),
      valueFormatter: (value: any) =>
        value ? moment(value).format("MMM DD YYYY HH:mm") : "-",
    },
    {
      field: "breached",
      headerName: "Breached",
      flex: 1,
      minWidth: 90,
      type: "boolean",
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.row.breached ? (
          <StyledDot style={{ backgroundColor: '#dc2626' }} />
        ) : null,
    },
    {
      field: "invalid",
      headerName: "Status",
      flex: 1,
      minWidth: 90,
      type: "singleSelect",
      align: "center",
      headerAlign: "center",
      valueOptions: [
        { value: true, label: "Invalid" },
        { value: false, label: "Valid" },
      ],
      renderCell: (params) => (params.row.invalid ? "Invalid" : "Valid"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.row.invalid ? (
          <div onMouseDown={(e) => e.stopPropagation()}>
            <Button
              variant="primary"
              height="36px"
              onClick={(e) => {
                e.stopPropagation();
                handleRevalidate(params.row);
              }}
            >
              Revalidate
            </Button>
          </div>
        ) : (
          <div onMouseDown={(e) => e.stopPropagation()}>
            <Button
              variant="warning"
              height="36px"
              onClick={(e) => {
                e.stopPropagation();
                setTargetReading(params.row);
              }}
            >
              Invalidate
            </Button>
          </div>
        ),
    },
  ], [memberMap]);

  const handleRevalidate = (row: ReadingRow) => {
    API.kore.readings
      .invalidate(row.id, { invalid: false, reason: "revalidation" }, Session.crackCookieToken("provider"))
      .then(() => loadReadings())
      .catch(() => setSnack({ msg: "Couldn't revalidate reading", severity: "error" }));
  };

  return (
    <Box sx={{ height: "96%", width: "100%" }}>
      <DataGridPro
        rows={rows}
        columns={columns}
        loading={loading}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            selectedCount: selectedIds.length,
            onBulkClick: () => setBulkOpen(true),
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={(model) => setSelectedIds(model as string[])}
        rowSelectionModel={selectedIds}
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
          sorting: { sortModel: [{ field: "measuredAt", sort: "desc" }] },
        }}
      />

      {/*For individual readings*/}
      <InvalidateModal
        open={!!targetReading}
        onClose={() => setTargetReading(null)}
        readingId={targetReading?.id}
        patientName={
          targetReading
            ? memberMap[targetReading.memberId] ?? targetReading.externId
            : undefined
        }
        onConfirm={async (reason) => {
          if (!targetReading) return;
          try {
            await API.kore.readings.invalidate(
              targetReading.id,
              { invalid: true, reason },
              Session.crackCookieToken("provider")
            );
            await loadReadings();
            setSnack({ msg: "Reading invalidated", severity: "success" });
          } catch {
            setSnack({ msg: "Couldn't invalidate reading", severity: "error" });
          }
        }}
      />

      {/*For bulk selection of readings*/}
      <InvalidateModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        requireReason={invalidateCount > 0}
        title={
          invalidateCount > 0 && revalidateCount > 0
            ? "Update Reading Validity"
            : revalidateCount > 0
              ? `Revalidate Reading${revalidateCount !== 1 ? "s" : ""}`
              : `Invalidate Reading${invalidateCount !== 1 ? "s" : ""}`
        }
        description={
          <>
            {invalidateCount > 0 && (
              <>{invalidateCount} reading{invalidateCount !== 1 ? "s" : ""} will be invalidated. </>
            )}
            {revalidateCount > 0 && (
              <>{revalidateCount} reading{revalidateCount !== 1 ? "s" : ""} will be revalidated.</>
            )}
          </>
        }
        onConfirm={async (reason) => {
          const token = Session.crackCookieToken("provider");

          const ops = [
            ...toInvalidate.map((reading) => ({
              id: reading.id,
              promise: API.kore.readings.invalidate(reading.id, { invalid: true, reason }, token),
            })),
            ...toRevalidate.map((reading) => ({
              id: reading.id,
              promise: API.kore.readings.invalidate(
                reading.id,
                { invalid: false, reason: reading.reason ?? "sync_error" },
                token
              ),
            })),
          ];

          const results = await Promise.allSettled(ops.map((op) => op.promise));
          const failed = results.filter((r) => r.status === "rejected").length;

          setSelectedIds([]);
          await loadReadings();

          if (failed > 0) {
            setSnack({ msg: `${failed} of ${ops.length} update${ops.length !== 1 ? "s" : ""} failed`, severity: "error" });
          } else {
            setSnack({ msg: `${ops.length} reading${ops.length !== 1 ? "s" : ""} updated`, severity: "success" });
          }
        }}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

function formatRow(reading: any): ReadingRow {
  return {
    id: reading.id ?? reading._id,
    memberId: reading.memberId ?? "",
    externId: reading.externId ?? "",
    type: reading.type,
    value: formatValue(reading),
    valueDetails: formatDetails(reading),
    measuredAt: reading.measuredAt,
    breached: !!reading.breached,
    invalid: !!reading.validation?.invalid,
    reason: reading.validation?.reason,
  };
}

function formatValue(reading: any): string {
  switch (reading.type) {
    case "bp":
      return `${reading.metrics?.systolic}/${reading.metrics?.diastolic} mmHg`;
    case "bgl":
      return `${reading.metrics?.glucose} mg/dL`;
    case "hrt":
      return `${reading.metrics?.pulse} bpm`;
    case "temp":
      return `${reading.metrics?.temp} ${reading.metrics?.units ?? ""}`;
    case "wt":
      return `${reading.metrics?.weight} lbs`;
    case "spo2":
      return `${reading.metrics?.spo2}%`;
    case "activity":
      return `${reading.metrics?.steps} steps`;
    case "sleep":
      return `${reading.metrics?.sleep} hours`;
    case "spir":
      return "...";
    default:
      return "-";
  }
}